import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import MwModal from "../components/ui/MwModal";
import MwButton from "../components/ui/MwButton";
import { printableDiv } from "../utils/global";
import CurrencyContext from "../utils/CurrencyHelper";

export const JournalEntryPrintScreen = ({
  onShow = false,
  setOnShow = () => {},
  entry = null,
  autoPrint = false,
  onAutoPrintHandled = () => {},
}) => {
  const [t] = useTranslation("global");
  const printRef = useRef(null);
  const lang = i18next.language === "ar" ? "ar" : "en";
  const isRtl = lang === "ar";
  const { currencies = [] } = useContext(CurrencyContext);

  const lines = useMemo(() => {
    if (Array.isArray(entry?.details)) return entry.details;
    if (Array.isArray(entry?.detail_relation)) return entry.detail_relation;
    if (Array.isArray(entry?.lines)) return entry.lines;
    return [];
  }, [entry?.details, entry?.detail_relation, entry?.lines]);

  const totalDebit = useMemo(
    () => lines.reduce((sum, line) => sum + Number(line.trans_de || 0), 0),
    [lines]
  );

  const totalCredit = useMemo(
    () => lines.reduce((sum, line) => sum + Number(line.trans_cr || 0), 0),
    [lines],
  );

  const currencyMeta = useMemo(
    () =>
      currencies.find(
        (currency) =>
          String(currency?.id) === String(entry?.trans_currency) ||
          String(currency?.value) === String(entry?.trans_currency),
      ) || null,
    [currencies, entry?.trans_currency],
  );
  const currencyCode =
    currencyMeta?.code || String(entry?.trans_currency || "").trim() || "-";
  const currencyLabel = i18next.language==="ar" ?  currencyMeta?.name_ar :  currencyMeta?.name_en ;
    
  const moneyFormatter = useMemo(() => {
    const locale = lang === "ar" ? "en-US" : "en-US";
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [lang]);

  const formatMoney = useCallback(
    (value) => moneyFormatter.format(Number(value || 0)),
    [moneyFormatter],
  );
  const formatMoneyWithCurrency = useCallback(
    (value) => `${formatMoney(value)} ${currencyLabel}`,
    [formatMoney, currencyCode , currencyLabel],
  );

  const statusText =
    entry?.is_completed
      ? t("entriesFilter.posted")
      : t("entriesFilter.unposted");

  const handlePrint = useCallback(() => {
    if (!printRef.current) return;
    printableDiv(printRef, lang);
  }, [lang]);

  useEffect(() => {
    if (onShow && autoPrint && entry) {
      handlePrint();
      onAutoPrintHandled();
    }
  }, [onShow, autoPrint, entry, handlePrint, onAutoPrintHandled]);

  const entryNo = entry?.trans_no || entry?.id || "-";
  const title = `${t("dropdown.show")} ${t("JournalEntry.numofentry")} ${entryNo}`;

  return (
    <MwModal onShow={onShow} setOnShow={setOnShow} custWidth={" "} >
      <div className="flex flex-col w-full h-full bg-slate-50" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-slate-300">
          <button
            onClick={() => setOnShow(false)}
            className="w-10 h-10 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            X
          </button>

          <h3 className="text-[#261759] font-bold text-lg">{title}</h3>

          <div className="flex items-center gap-2">
            <MwButton type="secondary" onClick={() => setOnShow(false)}>
              {t("common.cancel")}
            </MwButton>
            <MwButton type="primary" onClick={handlePrint}>
              {t("reports.print")}
            </MwButton>
          </div>
        </div>

        <div className="p-4">
          <div
            ref={printRef}
            id="journal-entry-print"
            className="bg-white border border-slate-200 rounded-2xl p-6 text-[#261759]"
          >
            <style>{`
              @media print {
                #journal-entry-print,
                #journal-entry-print table,
                #journal-entry-print th,
                #journal-entry-print td {
                  color: #111827 !important;
                }
              }
            `}</style>
            <div className="grid grid-cols-5 gap-4 text-center pb-4 border-b border-slate-200">
              <div>
                <div className="text-xs text-slate-500">{t("JournalEntry.numofentry")}</div>
                <div className="text-lg font-semibold mt-1">{entry?.trans_no || "-"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{t("JournalEntry.date")}</div>
                <div className="text-lg mt-1">{entry?.trans_date_m || "-"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{t("JournalEntry.previosnum")}</div>
                <div className="text-lg mt-1">
                  {entry?.trans_ref_no || (isRtl ? "لا يوجد" : "N/A")}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{t("JournalEntry.currency")}</div>
                <div className=" mt-1">
                  {currencyLabel} 
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">{t("entriesFilter.state")}</div>
                <div
                  className={`text-lg mt-1 ${
                    entry?.is_completed
                      ? "text-emerald-600"
                      : "text-amber-600"
                  }`}
                >
                  {statusText}
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-xs text-slate-500">
                {t("JournalEntry.description")}
              </div>
              <div className="mt-1 text-sm font-semibold text-[#261759]">
                {entry?.trans_desc || "-"}
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-100 text-[#261759]">
                  <tr className="text-center">
                    <th className="py-2 px-1 w-6">#</th>
                    <th className="py-2 px-1 w-32">{t("JournalEntry.accountNumber")}</th>
                    <th className="py-2 px-1 text-start w-44">{t("JournalEntry.accountName")}</th>
                    <th className="py-2 px-1 w-20">{t("JournalEntry.debit")}</th>
                    <th className="py-2 px-1 w-20">{t("JournalEntry.credit")}</th>
                    <th className="py-2 px-1 text-start w-44">{t("accounts.costCenterAccount")}</th>
                    <th className="py-2 px-1 flex-1 text-start">{t("JournalEntry.notes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr
                      key={`${line.account_no || index}-${index}`}
                      className="border-b border-slate-200"
                    >
                      <td className="py-2 px-1 text-center w-6">{index + 1}</td>
                      <td className="py-2 px-1 text-center w-32">{line.account_no || "-"}</td>
                      <td className="py-2 px-1 text-start w-44">{line.accountName || "-"}</td>
                      <td className="py-2 px-1 text-center w-20">
                        {formatMoney(line.trans_de)}
                      </td>
                      <td className="py-2 px-1 text-center w-20">
                        {formatMoney(line.trans_cr)}
                      </td>
                      <td className="py-2 px-1 text-start w-44">
                        {line.cost_center_no
                          ? `${line.cost_center_no} - ${line.costCenterLabel || "-"}`
                          : "-"}
                      </td>
                      <td className="py-2 px-1 text-start flex-1">{line.trans_notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[12px]">
              <div className="flex flex-wrap items-center justify-between gap-2 text-slate-700">
                <span>
                  {t("JournalEntry.numberOfMovements")}:{" "}
                  <strong className="text-[#261759]">{lines.length}</strong>
                </span>
                <span>
                  {t("JournalEntry.totalDebit")}:{" "}
                  <strong className="text-[#261759]">
                    {formatMoneyWithCurrency(totalDebit)}
                  </strong>
                </span>
                <span>
                  {t("JournalEntry.totalCredit")}:{" "}
                  <strong className="text-[#261759]">
                    {formatMoneyWithCurrency(totalCredit)}
                  </strong>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MwModal>
  );
};

export default JournalEntryPrintScreen;
